import React, { useState, useRef, useEffect, useCallback } from 'react';
import './TestimonialsSection.css';

const TestimonialsSection: React.FC = () => {
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);

  // Wykrywanie mobile dla responsywnych wymiarów kart (z debounce)
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const checkMobile = () => {
      // Debounce 150ms - zapobiega re-renderom przy zmianie paska adresu na iOS
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setIsMobile(window.innerWidth <= 480);
      }, 150);
    };

    // Ustaw początkową wartość bez debounce
    setIsMobile(window.innerWidth <= 480);

    window.addEventListener('resize', checkMobile);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const testimonials = [
    {
      name: "Anna K.",
      role: "Uczennica klasy 3 liceum",
      course: "Pakiet Premium - Przygotowanie do matury",
      rating: 5,
      text: "Dzięki kursom w Sigma Study zdałam maturę rozszerzoną z matematyki na 98%! Nauczyciele są fantastyczni, wszystko tłumaczą w sposób zrozumiały. Polecam każdemu, kto chce naprawdę zrozumieć matematykę.",
      avatarLetter: "A",
      avatarColor: "#e91e63",
      result: "Matura rozszerzona: 98%"
    },
    {
      name: "Piotr N.",
      role: "Rodzic ucznia klasy 8",
      course: "Pakiet Podstawowy - Matematyka gimnazjalna",
      rating: 5,
      text: "Mój syn Kacper miał ogromne problemy z matematyką. Po trzech miesiącach nauki w Sigma Study jego oceny poprawiły się z 2 na 4+. Nauczyciele mają niesamowite podejście do dzieci!",
      avatarLetter: "P",
      avatarColor: "#2196f3",
      result: "Poprawa ocen z 2 na 4+"
    },
    {
      name: "Maria Z.",
      role: "Studentka matematyki",
      course: "Pakiet VIP - Matematyka wyższa",
      rating: 5,
      text: "Indywidualne lekcje z Kornelem to była najlepsza inwestycja w moją edukację. Analiza matematyczna przestała być koszmarem, a stała się moim ulubionym przedmiotem. Serdecznie polecam!",
      avatarLetter: "M",
      avatarColor: "#4caf50",
      result: "Średnia ocen: 4.8/5.0"
    },
    {
      name: "Janusz K.",
      role: "Dorosły uczestnik kursu",
      course: "Matematyka dla dorosłych",
      rating: 5,
      text: "Miałem przerwę w studiach i nie byłem w stanie poradzić sobie z matematyka numeryczną na 4tym roku. Dzięki Łukaszowi nadrobiłem braki, które miałem jeszcze z poprzednich poziomów nauczania i na koniec miałem 4!",
      avatarLetter: "J",
      avatarColor: "#ff9800",
      result: "Ukończenie kursu z wyróżnieniem"
    },
    {
      name: "Ola W.",
      role: "Uczennica klasy 6",
      course: "Szkoła podstawowa",
      rating: 5,
      text: "Matematyka wcześniej była dla mnie bardzo trudna, ale dzięki panu Oskarowi z Sigma Study teraz to mój ulubiony przedmiot! Lekcje są ciekawe i pełne zaangażowania.",
      avatarLetter: "O",
      avatarColor: "#9c27b0",
      result: "Ocena wzrosła z 3 na 5"
    },
    {
      name: "Janek A.",
      role: "Uczeń przygotowujący się do olimpiady",
      course: "Przygotowanie do konkursów",
      rating: 5,
      text: "Kornel przygotował mnie do Olimpiady Matematycznej Juniorów. Dzięki jego wsparciu zostałem laureatem 2 stopnia! W liceum przygotowuję się do Olimpiady Matematycznej.",
      avatarLetter: "R",
      avatarColor: "#f44336",
      result: "Laureat Olimpiady Matematycznej Juniorów"
    }
  ];

  // Na mobile karta zajmuje szerokość ekranu minus strzałki i padding
  // Na desktop stała szerokość 420px
  const cardWidth = isMobile ? (typeof window !== 'undefined' ? window.innerWidth - 104 : 270) : 420;
  const gap = isMobile ? 12 : 24;
  const totalWidth = cardWidth + gap;

  // Tworzymy 3 kopie kafelków dla efektu nieskończoności
  const extendedTestimonials = [...testimonials, ...testimonials, ...testimonials];

  // Zaczynamy od środkowej kopii
  const [actualIndex, setActualIndex] = useState(testimonials.length);

  // Obliczamy wyświetlany indeks (0 do length-1)
  const displayIndex = actualIndex % testimonials.length;

  const handlePrev = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setActualIndex(prev => prev - 1);
  }, [isTransitioning]);

  const handleNext = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setActualIndex(prev => prev + 1);
  }, [isTransitioning]);

  // Teleportacja po zakończeniu animacji
  const handleTransitionEnd = useCallback(() => {
    setIsTransitioning(false);
    const total = testimonials.length;

    // Jeśli wyszliśmy poza środkowy zestaw, teleportujemy się
    if (actualIndex >= total * 2) {
      setActualIndex(actualIndex - total);
    } else if (actualIndex < total) {
      setActualIndex(actualIndex + total);
    }
  }, [actualIndex, testimonials.length]);

  const goToSlide = (index: number) => {
    if (isTransitioning) return;
    // Obliczamy różnicę i przesuwamy relatywnie
    const diff = index - displayIndex;
    setIsTransitioning(true);
    setActualIndex(prev => prev + diff);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (isTransitioning) return;
    setIsDragging(true);
    setStartX(e.clientX);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    const diff = e.clientX - startX;
    setDragOffset(diff);
  };

  const handleMouseUp = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);

    if (dragOffset > 80) {
      handlePrev();
    } else if (dragOffset < -80) {
      handleNext();
    }
    setDragOffset(0);
  }, [isDragging, dragOffset, handlePrev, handleNext]);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (isTransitioning) return;
    setIsDragging(true);
    setStartX(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    const diff = e.touches[0].clientX - startX;
    setDragOffset(diff);
  };

  const handleTouchEnd = () => {
    handleMouseUp();
  };

  useEffect(() => {
    const handleMouseUpGlobal = () => {
      if (isDragging) {
        handleMouseUp();
      }
    };
    window.addEventListener('mouseup', handleMouseUpGlobal);
    return () => window.removeEventListener('mouseup', handleMouseUpGlobal);
  }, [isDragging, handleMouseUp]);

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, index) => (
      <span key={index} className={`star ${index < rating ? 'filled' : ''}`}>
        ★
      </span>
    ));
  };

  return (
    <section id="testimonials" className="testimonials-section">
      <div className="testimonials-container">
        <div className="section-header">
          <span className="section-badge">Opinie</span>
          <h2>Co Mówią Nasi Uczniowie</h2>
          <p>Przeczytaj opinie osób, które już osiągnęły sukces dzięki naszym kursom</p>
        </div>

        <div className="carousel-wrapper">
          <button
            className="carousel-btn carousel-btn-prev"
            onClick={handlePrev}
            aria-label="Poprzednia opinia"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>

          <div
            className="carousel-viewport"
            ref={carouselRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div
              className="carousel-track"
              style={{
                transform: `translateX(calc(-${actualIndex * totalWidth}px + ${dragOffset}px))`,
                transition: isDragging ? 'none' : (isTransitioning ? 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none')
              }}
              onTransitionEnd={handleTransitionEnd}
            >
              {extendedTestimonials.map((testimonial, index) => (
                <div
                  key={index}
                  className={`testimonial-card ${index === actualIndex ? 'active' : ''}`}
                >
                  <div className="card-quote-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
                    </svg>
                  </div>

                  <blockquote className="testimonial-text">
                    {testimonial.text}
                  </blockquote>

                  <div className="testimonial-result">
                    <span className="result-icon">🎯</span>
                    <strong>{testimonial.result}</strong>
                  </div>

                  <div className="testimonial-footer">
                    <div
                      className="testimonial-avatar"
                      style={{ backgroundColor: testimonial.avatarColor }}
                    >
                      <span className="avatar-letter">{testimonial.avatarLetter}</span>
                    </div>
                    <div className="testimonial-info">
                      <h4 className="testimonial-name">{testimonial.name}</h4>
                      <p className="testimonial-role">{testimonial.role}</p>
                      <div className="testimonial-rating">
                        {renderStars(testimonial.rating)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button
            className="carousel-btn carousel-btn-next"
            onClick={handleNext}
            aria-label="Następna opinia"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>

        <div className="carousel-dots">
          {testimonials.map((_, index) => (
            <button
              key={index}
              className={`carousel-dot ${index === displayIndex ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Przejdź do opinii ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
